import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { t } from "@/i18n";
import { Button, Card, Badge, Field, Input, Spinner, EmptyState } from "@/components/ui";
import {
  useAssignment,
  useAssignmentTypes,
  useClassrooms,
  useConnections,
  useAssignHomework,
  useHomeworkStatus,
  type Classroom,
  type HomeworkStatusRow,
} from "@/lib/queries";

export const Route = createFileRoute("/app/assign/$id")({
  component: AssignPage,
});

type PostResult = { classroomId: string; status: string; error?: string };

function AssignPage() {
  const { id } = Route.useParams();
  const { data: a, isLoading } = useAssignment(id);
  const { data: types } = useAssignmentTypes();
  const connections = useConnections();
  const classrooms = useClassrooms();
  const assign = useAssignHomework();
  const status = useHomeworkStatus(id);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dueDate, setDueDate] = useState("");
  const [results, setResults] = useState<PostResult[]>([]);

  const grouped = useMemo(() => {
    const map = new Map<string, { label: string; email: string; rooms: Classroom[] }>();
    for (const room of classrooms.data ?? []) {
      if (!map.has(room.googleConnectionId)) {
        map.set(room.googleConnectionId, {
          label: room.connectionLabel || room.connectionEmail,
          email: room.connectionEmail,
          rooms: [],
        });
      }
      map.get(room.googleConnectionId)!.rooms.push(room);
    }
    return [...map.values()];
  }, [classrooms.data]);

  function toggle(roomId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(roomId)) next.delete(roomId);
      else next.add(roomId);
      return next;
    });
  }

  // Preview: the Classroom post title is just the assignment title now — the
  // homework-type label ("Tarea") is applied as the Classroom Topic.
  function previewFor(_room: Classroom): string {
    return a?.title ?? "";
  }

  const roomName = (rid: string) =>
    (classrooms.data ?? []).find((r) => r.id === rid)?.name ?? rid;

  if (isLoading) return <Spinner label={t("app.loading")} />;
  if (!a) return <p className="text-destructive">{t("repo.notFound")}</p>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{t("assign.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {a.title} · {a.typeName}
          </p>
        </div>
        <Link to="/app/assignments">
          <Button variant="ghost">← {t("assign.backToRepo")}</Button>
        </Link>
      </div>

      {(classrooms.data ?? []).length === 0 ? (
        <EmptyState>
          {t("assign.noClassrooms")}{" "}
          <Link to="/app/classes" search={{ connected: false, error: undefined }} className="underline">
            {t("assign.connectFirst")}
          </Link>
        </EmptyState>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            {grouped.map((g) => (
              <Card key={g.email + g.label} className="p-5">
                <p className="mb-3 font-semibold">
                  {g.label} <span className="text-xs text-muted-foreground">({g.email})</span>
                </p>
                <ul className="space-y-2">
                  {g.rooms.map((room) => (
                    <li key={room.id} className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={selected.has(room.id)}
                        onChange={() => toggle(room.id)}
                        id={`room-${room.id}`}
                      />
                      <label htmlFor={`room-${room.id}`} className="flex-1 cursor-pointer">
                        {room.name}
                      </label>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>

          <AssignSidebar
            selected={selected}
            dueDate={dueDate}
            setDueDate={setDueDate}
            pending={assign.isPending}
            results={results}
            history={status.data ?? []}
            roomName={roomName}
            previewFor={previewFor}
            onConfirm={() =>
              void (async () => {
                if (!a || selected.size === 0) return;
                const res = await assign.mutateAsync({
                  assignmentId: a.id,
                  classroomIds: [...selected],
                  dueDate: dueDate || null,
                });
                setResults(res.results);
              })()
            }
          />
        </div>
      )}
    </div>
  );
}

function AssignSidebar({
  selected,
  dueDate,
  setDueDate,
  pending,
  results,
  history,
  roomName,
  previewFor,
  onConfirm,
}: {
  selected: Set<string>;
  dueDate: string;
  setDueDate: (v: string) => void;
  pending: boolean;
  results: PostResult[];
  history: HomeworkStatusRow[];
  roomName: (rid: string) => string;
  previewFor: (room: Classroom) => string;
  onConfirm: () => void;
}) {
  const rooms = useClassrooms().data ?? [];
  return (
    <div className="space-y-4">
      <Card className="space-y-4 p-5">
        <Field label={t("assign.dueDate")}>
          <Input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
        </Field>
        <p className="text-xs text-muted-foreground">{t("assign.dueOptional")}</p>
        <div className="rounded-lg bg-muted/40 p-3 text-sm">
          <p className="mb-1 font-medium">{t("assign.preview")}</p>
          {[...selected].slice(0, 4).map((rid) => {
            const room = rooms.find((r) => r.id === rid);
            return room ? <p key={rid}>• {previewFor(room)}</p> : null;
          })}
          {selected.size > 4 && (
            <p className="text-xs text-muted-foreground">
              +{selected.size - 4} {t("assign.more")}
            </p>
          )}
          {selected.size === 0 && (
            <p className="text-xs text-muted-foreground">{t("assign.noneSelected")}</p>
          )}
        </div>
        <Button
          className="w-full"
          disabled={selected.size === 0 || pending}
          onClick={onConfirm}
        >
          {pending ? t("assign.posting") : t("assign.confirm", { count: selected.size })}
        </Button>
      </Card>

      {results.length > 0 && (
        <Card className="space-y-2 p-5">
          <p className="font-semibold">{t("assign.results")}</p>
          {results.map((r) => (
            <p key={r.classroomId} className="flex flex-wrap items-center gap-2 text-sm">
              {r.status === "posted" ? (
                <Badge tone="green">✓ {t("assign.posted")}</Badge>
              ) : (
                <Badge tone="red">✕ {t("assign.failed")}</Badge>
              )}
              <span>{roomName(r.classroomId)}</span>
              {r.error && <span className="text-xs text-muted-foreground">{r.error}</span>}
            </p>
          ))}
        </Card>
      )}

      {history.length > 0 && (
        <Card className="space-y-2 p-5">
          <p className="font-semibold">{t("assign.history")}</p>
          {history.map((row) => (
            <p key={row.id} className="flex items-center gap-2 text-sm">
              {row.status === "posted" ? (
                <Badge tone="green">✓</Badge>
              ) : (
                <Badge tone="red">✕</Badge>
              )}
              <span>{row.classroomName || row.classroomId}</span>
            </p>
          ))}
        </Card>
      )}
    </div>
  );
}